import React, { useState } from 'react';
import { 
  Container, 
  Box, 
  Paper, 
  Typography, 
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import useApplicationForm from './useApplicationForm';
import TabsContainer from './TabsContainer';
import ValidationDialog from './ValidationDialog';
import RecruitmentPackModal from '../../../components/Counsellor/RecruitmentPackModal';
import dayjs from '../../../utils/dayjsConfig';
import {
  handlePersonalInfoChange,
  handleCounsellingInfoChange,
  handleEducationInfoChange,
  handleRefereeInfoChange
} from './SectionHandlers';

const ApplicationForm: React.FC = () => {
  const [recruitmentPackOpen, setRecruitmentPackOpen] = useState(false);
  
  const {
    activeTab,
    formData,
    setFormData,
    saveStatus,
    setSaveStatus,
    isSubmitted,
    expirationDate,
    error,
    loading,
    submitting,
    validationErrors,
    validationDialogOpen,
    setValidationDialogOpen,
    token,
    handleTabChange,
    handleTabNavigation,
    handleSubmitForm,
    navigateToSectionWithErrors
  } = useApplicationForm();

  // Wrapper functions to pass formData and setFormData to the handlers
  const handlePersonalInfoChangeWrapper = (data: any) => {
    handlePersonalInfoChange(formData, setFormData, data);
  };

  const handleCounsellingInfoChangeWrapper = (field: string, value: any) => {
    handleCounsellingInfoChange(formData, setFormData, field, value);
  };

  const handleEducationInfoChangeWrapper = (field: string, value: any) => {
    handleEducationInfoChange(formData, setFormData, field, value);
  };

  const handleRefereeInfoChangeWrapper = (data: any) => {
    handleRefereeInfoChange(formData, setFormData, data);
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ my: 4 }}>
        <Paper elevation={3} sx={{ p: 3 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              {isSubmitted ? (
                <>
                  <Typography variant="h5" color="primary" gutterBottom>
                    Application Already Submitted
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 2 }}>
                    Your application has been received and is being reviewed. You will be notified of the outcome via email.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                    If you have any questions or need to update your information, please contact our support team.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 3 }}
                    onClick={() => window.close()}
                  >
                    Close Window
                  </Button>
                </>
              ) : (
                <Typography variant="h5" color="error" gutterBottom>
                  {error}
                </Typography>
              )}
            </Box>
          ) : (
            <>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ flex: 1 }}>
                  Counsellor Application Form
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<DescriptionIcon />}
                  onClick={() => setRecruitmentPackOpen(true)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: '#2196f3',
                    color: '#2196f3',
                    '&:hover': {
                      borderColor: '#1976d2',
                      backgroundColor: 'rgba(33, 150, 243, 0.1)'
                    }
                  }}
                >
                  View Recruitment Pack
                </Button>
              </Box>
              {expirationDate && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  This form expires on {dayjs(expirationDate).format('DD/MM/YYYY')}
                </Alert>
              )}
              
              <TabsContainer
                activeTab={activeTab}
                handleTabChange={handleTabChange}
                formData={formData}
                setFormData={setFormData}
                saveStatus={saveStatus}
                setSaveStatus={setSaveStatus}
                setActiveTab={handleTabNavigation}
                token={token}
                setError={(error) => {}} // This is handled in the useApplicationForm hook
                isSubmitted={isSubmitted}
                submitting={submitting}
                handleSubmitForm={handleSubmitForm}
                handlePersonalInfoChange={handlePersonalInfoChangeWrapper}
                handleCounsellingInfoChange={handleCounsellingInfoChangeWrapper}
                handleEducationInfoChange={handleEducationInfoChangeWrapper}
                handleRefereeInfoChange={handleRefereeInfoChangeWrapper}
              />
            </>
          )}
        </Paper>
      </Box>
      
      <ValidationDialog
        open={validationDialogOpen}
        onClose={() => setValidationDialogOpen(false)}
        validationErrors={validationErrors}
        onNavigateToSection={navigateToSectionWithErrors}
      />
      
      <RecruitmentPackModal
        open={recruitmentPackOpen}
        onClose={() => setRecruitmentPackOpen(false)}
      />
    </Container>
  );
};

export default ApplicationForm;
